import React, { useState } from 'react';
import PropTypes from 'prop-types';
// components
import Button from '../Button';
// icons and mui components
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { CircularProgress, Popover } from '@mui/material';
// services
import api from '../../services/api';
import { downloadBlobAsFile } from './lib';
// constants
import { FILE_EXTENSIONS_TYPE, OPEN_XML_SPREADSHEET_MIME_TYPE } from './constants';
import { DOWNLOAD_URL } from '../../constants/api';
import { OverallPerformanceConstants } from '../../pages/overallPerformance/constant/overallPerformanceConstant';

export const RegionAndBanchDownloadButton = ({
  fileType = OPEN_XML_SPREADSHEET_MIME_TYPE,
  disabled = false,
  spendToggleState = false,
  branchDownloadFilters,
  regionDownloadFilters,
  localSpendFilters,
  showRegionButton,
  subBranchDashboard = false,
}) => {
  const [reportIsDownloading, setReportIsDownloading] = useState(false);
  const [containerEl, setContainerEl] = useState(null);

  const handleOpen = (e) => {
    setContainerEl(e.currentTarget);
  };

  const handleClose = () => {
    setContainerEl(null);
  };

  const open = Boolean(containerEl);

  const styles = {
    '&:hover': {
      backgroundColor: '#C5C3C3',
    },
    color: '#000',
    fontSize: 12,
    textTransform: 'none',
  };

  const handleDownload = async (downloadType) => {
    setReportIsDownloading(true);
    try {
      // Create a copy of filters with downloadType
      let downloadFilters;
      // Determine filename based on download type and toggle state
      let downloadFilename;
      if (downloadType === 'branch') {
        if (spendToggleState) {
          downloadFilename = subBranchDashboard
            ? OverallPerformanceConstants.OVERALL_PERFORMANCE_LOCAL_SPEND_BRANCHES_FILE_NAME_SUB_BRANCH
            : OverallPerformanceConstants.OVERALL_PERFORMANCE_LOCAL_SPEND_BRANCHES_FILE_NAME;
        } else {
          downloadFilename = subBranchDashboard
            ? OverallPerformanceConstants.OVERALL_PERFORMANCE_BRANCHES_FILE_NAME_SUB_BRANCH
            : OverallPerformanceConstants.OVERALL_PERFORMANCE_BRANCHES_FILE_NAME;
        }
        downloadFilters = spendToggleState ? localSpendFilters : branchDownloadFilters;
      } else if (downloadType === 'region') {
        downloadFilename = OverallPerformanceConstants.OVERALL_PERFORMANCE_REGION_FILE_NAME;
        downloadFilters = regionDownloadFilters;
      }

      const response = await api.post(DOWNLOAD_URL, downloadFilters, {
        responseType: 'blob',
      });

      let type = fileType;
      if (!type) {
        const splits = downloadFilename.split('.');
        type = FILE_EXTENSIONS_TYPE[splits[splits.length - 1]];
      }
      if (!type) {
        throw new Error(
          'Could not determine file type, either pass `fileType` as `props` or provide a valid file extension and type mapping in `constants/index`'
        );
      }

      downloadBlobAsFile(response?.data, downloadFilename, type);
    } catch (ex) {
      console.error('Error downloading file:', ex);
    } finally {
      setReportIsDownloading(false);
    }
  };

  return (
    <div>
      <Button
        onMouseEnter={handleOpen}
        disabled={disabled}
        style={{
          display: 'flex',
          gap: '15px',
          backgroundColor: '#e4e4e7',
          marginTop: '12px',
          height: '40px',
        }}
      >
        {reportIsDownloading ? (
          <CircularProgress size="2rem" sx={{ color: '#000' }} />
        ) : (
          <CloudDownloadIcon />
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={containerEl}
        onClose={handleClose}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              handleDownload('branch');
              handleClose();
            }}
            disabled={disabled || reportIsDownloading}
            sx={styles}
          >
            Download by Branch
          </Button>
          {showRegionButton && (
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                handleDownload('region');
                handleClose();
              }}
              disabled={disabled || reportIsDownloading}
              sx={styles}
            >
              Download by Region
            </Button>
          )}
        </div>
      </Popover>
    </div>
  );
};

// Prop validation
RegionAndBanchDownloadButton.propTypes = {
  fileType: PropTypes.string,
  disabled: PropTypes.bool,
  spendToggleState: PropTypes.bool,
  branchDownloadFilters: PropTypes.object,
  regionDownloadFilters: PropTypes.object,
  localSpendFilters: PropTypes.object,
  showRegionButton: PropTypes.bool,
  subBranchDashboard: PropTypes.bool,
};
